import React, { useState } from 'react';
import { api } from '../lib/api';
import type { Employee, Invitation, Role, Task } from '../lib/types';
import { useResource } from '../lib/hooks';
import { Confirm, Drawer, Stat, TaskRow, type Ctx } from '../main';

const ROLE: Record<Role, string> = {
  specialist: 'Specialist — field work, their own tasks',
  manager: 'Manager — assigns work, sees the money',
  admin: 'Admin — everything, including the team',
};

export function Team({ ctx, tasks, employees }: {
  ctx: Ctx; tasks: Task[]; employees: Employee[];
}): React.JSX.Element {
  const [open, setOpen] = useState<Employee | null>(null);
  const [moving, setMoving] = useState<Task | null>(null);
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState<Employee | null>(null);
  const [removing, setRemoving] = useState<Employee | null>(null);
  // waiting to accept: each holds a seat, so a full plan is explained here
  const invitations = useResource(() => (ctx.canManage ? api.invitations() : Promise.resolve(null)), [ctx.canManage]);
  const [busy, setBusy] = useState<string | null>(null);
  const act = async (inv: Invitation, fn: () => Promise<unknown>, title: string, detail: string): Promise<void> => {
    setBusy(inv.id);
    try { await fn(); await invitations.reload(); ctx.toast(title, detail); }
    catch (e) { ctx.toast('Could not do that', (e as Error).message); }
    finally { setBusy(null); }
  };
  const pending = invitations.data?.invitations ?? [];

  const total = employees.reduce((a, b) => a + Number(b.open_tasks), 0);
  const unassigned = tasks.filter((t) => !t.assigneeId);

  return (
    <>
      <div className="grid g3" style={{ marginBottom: 16 }}>
        <Stat label="People" value={employees.filter((e) => e.is_active).length} />
        <Stat label="Open tasks" value={total} />
        <Stat label="Unassigned" value={unassigned.length}
          tone={unassigned.length ? 'hot' : undefined} foot="Nobody is looking at these" />
      </div>

      {ctx.isAdmin && (
        <div className="bar">
          <button className="btn sm accent w" style={{ marginLeft: 'auto' }} onClick={() => setInviting(true)}>
            Invite someone
          </button>
        </div>
      )}

      <div className="card"><table className="responsive">
        <thead><tr><th>Name</th><th>Role</th><th>Open</th><th>Overdue</th><th>Status</th></tr></thead>
        <tbody>{employees.map((e) => (
          <tr key={e.id} className="clk" onClick={() => setOpen(e)}>
            <td data-label="Name"><b>{e.name}</b><div className="sub">{e.email}</div></td>
            <td data-label="Role"><span className="tag t-mute">{e.role}</span></td>
            <td data-label="Open" className="mono">{e.open_tasks}</td>
            <td data-label="Overdue" className={Number(e.overdue_tasks) ? 'late mono' : 'mono'}>
              {e.overdue_tasks}
            </td>
            <td data-label="Status">
              <span className={`tag t-${e.is_active ? 'ok' : 'mute'}`}>{e.is_active ? 'Active' : 'Inactive'}</span>
            </td>
          </tr>
        ))}</tbody>
      </table>
      {employees.length === 0 && <div className="empty"><b>Nobody yet</b>Invite your first specialist.</div>}
      </div>

      {pending.length > 0 && (
        <div className="card" style={{ marginTop: 14 }}>
          <div className="hd"><h3>Waiting to accept</h3><div className="r sub">Each holds a seat until accepted or withdrawn</div></div>
          <table className="responsive">
            <tbody>{pending.map((i) => (
              <tr key={i.id}>
                <td data-label="Name"><b>{i.name}</b><div className="sub">{i.email}</div></td>
                <td data-label="Role"><span className="tag t-mute">{i.role}</span></td>
                <td data-label="Link">
                  {i.expired
                    ? <span className="tag t-bad">Link expired</span>
                    : <span className="sub">Link valid until {new Date(i.expires_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>}
                  {i.invited_by && <div className="sub">invited by {i.invited_by}</div>}
                </td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {ctx.isAdmin && (
                    <>
                      <button className="btn sm w" disabled={busy === i.id}
                        onClick={() => act(i, () => api.resendInvitation(i.id), 'Invitation sent again',
                          `${i.name} has 48 hours from now. The earlier link no longer works.`)}>Resend</button>
                      <button className="btn sm quiet w" disabled={busy === i.id}
                        onClick={() => act(i, () => api.withdrawInvitation(i.id), 'Invitation withdrawn',
                          `${i.name}'s link no longer works, and the seat is free.`)}>Withdraw</button>
                    </>
                  )}
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      {unassigned.length > 0 && (
        <>
          <h2 className="sec">Unassigned work</h2>
          {unassigned.map((t) => (
            <TaskRow key={t.id} task={t} ctx={ctx}
              badge={ctx.canManage
                ? <span className="tag t-bad w" onClick={(ev) => { ev.stopPropagation(); setMoving(t); }}>assign</span>
                : undefined} />
          ))}
        </>
      )}

      {open && !moving && !editing && !removing && (
        <Drawer title={open.name} subtitle={`${open.role} · ${open.open_tasks} open${open.phone ? ` · ${open.phone}` : ''}`}
          onClose={() => setOpen(null)}
          footer={<>
            {ctx.isAdmin && open.id !== ctx.me.id && open.is_active && (
              <button className="btn quiet w" style={{ marginRight: 'auto', color: 'var(--coral)' }}
                onClick={() => setRemoving(open)}>Remove access</button>
            )}
            <button className="btn" onClick={() => setOpen(null)}>Close</button>
            {ctx.isAdmin && <button className="btn accent w" onClick={() => setEditing(open)}>Edit</button>}
          </>}>
          {Number(open.overdue_tasks) > 0 && (
            <div className="note bad" style={{ marginBottom: 14 }}>
              {open.overdue_tasks} of these are overdue. Consider moving work to someone with capacity.
            </div>
          )}
          {tasks.filter((t) => t.assigneeId === open.id).map((t) => (
            <div key={t.id}>
              <TaskRow task={t} ctx={ctx} />
              <div style={{ textAlign: 'right', marginTop: -4, marginBottom: 10 }}>
                {ctx.canManage && <button className="btn sm w" onClick={() => setMoving(t)}>Reassign this</button>}
              </div>
            </div>
          ))}
          {tasks.filter((t) => t.assigneeId === open.id).length === 0 && (
            <div className="note ok">Nothing open.</div>
          )}
        </Drawer>
      )}

      {moving && (
        <Drawer title="Reassign" subtitle={moving.name} onClose={() => setMoving(null)}
          footer={<button className="btn" onClick={() => setMoving(null)}>Cancel</button>}>
          <div className="note">The new assignee is notified. Their queue updates on their next sync.</div>
          <div style={{ marginTop: 14 }}>
            {employees.filter((e) => e.is_active && e.id !== moving.assigneeId).map((e) => (
              <button key={e.id} className="opt" onClick={async () => {
                try {
                  await api.reassign(moving.id, e.id);
                  await ctx.refresh();
                  ctx.toast('Reassigned', `${moving.name} → ${e.name}`);
                } catch (err) {
                  ctx.toast('Could not reassign', (err as Error).message);
                }
                setMoving(null); setOpen(null);
              }}>
                <span className="k" />
                <span>
                  <b>{e.name}</b>
                  <span>{e.open_tasks} open{Number(e.overdue_tasks) ? ` · ${e.overdue_tasks} late` : ' · on track'}</span>
                </span>
              </button>
            ))}
          </div>
        </Drawer>
      )}

      {inviting && <Invite ctx={ctx} onClose={() => setInviting(false)} onSent={() => invitations.reload()} />}
      {editing && (
        <EditEmployee ctx={ctx} employee={editing} onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); setOpen(null); await ctx.refresh(); }} />
      )}
      {removing && (
        <Confirm title={`Remove ${removing.name}’s access?`} danger action="Remove access"
          detail={Number(removing.open_tasks) > 0
            ? `${removing.name} still has ${removing.open_tasks} open task(s). Reassign them first — the API will refuse until then.`
            : 'They can no longer sign in. Everything they did stays attributed to them.'}
          onClose={() => setRemoving(null)}
          onConfirm={async () => {
            await api.removeEmployee(removing.id);
            setRemoving(null); setOpen(null);
            await ctx.refresh();
            ctx.toast('Access removed', removing.name);
          }} />
      )}
    </>
  );
}

function EditEmployee({ ctx, employee, onClose, onSaved }: {
  ctx: Ctx; employee: Employee; onClose: () => void; onSaved: () => Promise<void>;
}): React.JSX.Element {
  const self = employee.id === ctx.me.id;
  const [form, setForm] = useState({ name: employee.name, phone: employee.phone ?? '', role: employee.role });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (): Promise<void> => {
    setBusy(true); setError(null);
    try {
      await api.updateEmployee(employee.id, {
        // an emptied phone is sent as null, which clears it
        name: form.name, phone: form.phone || null,
        ...(!self && form.role !== employee.role ? { role: form.role } : {}),
      });
      ctx.toast('Saved', form.name);
      await onSaved();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <Drawer title={`Edit ${employee.name}`} subtitle={employee.email} onClose={onClose}
      footer={<>
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn accent" disabled={!form.name || busy} onClick={save}>{busy ? 'Saving…' : 'Save changes'}</button>
      </>}>
      {error && <div className="note bad" style={{ marginBottom: 14 }}>{error}</div>}
      <div className="field">
        <label htmlFor="ee-name">Name</label>
        <input id="ee-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
      </div>
      <div className="field">
        <label htmlFor="ee-phone">Phone</label>
        <input id="ee-phone" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
      </div>
      <div className="field">
        <label htmlFor="ee-role">Role</label>
        <select id="ee-role" value={form.role} disabled={self}
          onChange={(e) => setForm({ ...form, role: e.target.value as Role })}>
          {(Object.keys(ROLE) as Role[]).map((r) => <option key={r} value={r}>{ROLE[r]}</option>)}
        </select>
        {self && <div className="hint">Another admin has to change your own role — so nobody locks the team out by accident.</div>}
      </div>
    </Drawer>
  );
}

function Invite({ ctx, onClose, onSent }: { ctx: Ctx; onClose: () => void; onSent: () => Promise<void> }): React.JSX.Element {
  const [form, setForm] = useState({ name: '', email: '', role: 'specialist' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = async (): Promise<void> => {
    setBusy(true); setError(null);
    try {
      const sent = await api.invite(form);
      await Promise.all([ctx.refresh(), onSent()]);
      ctx.toast('Invitation sent', `${form.name} has ${sent.expiresInHours ?? 48} hours to accept.`);
      onClose();
    } catch (e) {
      // a seat-limit refusal names the numbers; show it as it came back
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <Drawer title="Invite someone" subtitle="They set their own password" onClose={onClose}
      footer={<>
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn accent" disabled={!form.name || !form.email || busy} onClick={send}>
          {busy ? 'Sending…' : 'Send invitation'}
        </button>
      </>}>
      {error && <div className="note bad" style={{ marginBottom: 14 }}>{error}</div>}
      <div className="field">
        <label htmlFor="iname">Name</label>
        <input id="iname" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
      </div>
      <div className="field">
        <label htmlFor="iemail">Work email</label>
        <input id="iemail" type="email" value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })} />
      </div>
      <div className="field">
        <label htmlFor="irole">Role</label>
        <select id="irole" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
          {(Object.keys(ROLE) as Role[]).map((r) => <option key={r} value={r}>{ROLE[r]}</option>)}
        </select>
      </div>
      <div className="note">
        We never set someone else's password. They get a link, valid for 48 hours, and choose
        their own; the email tells a specialist to sign in on the field app. If it lapses, resend it
        from <i>Waiting to accept</i>.
      </div>
    </Drawer>
  );
}
